import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import pick from 'lodash/pick';
import a from '../../../../../redux/actions';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';
import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';
import types from '../../../../../types';
import makeInputValue from '../../../../../lib/makeInputValue';

const vehicleTradeInOptions = (vehicles, t) => vehicles
  .filter(vehicle => !vehicle.isElectric)
  .map(vehicle => ({ value: vehicle.index, display: t('Vehicle {{index}}', vehicle) }));

const foodEmissionOptions = t => [
  { value: 1, display: t('Include emissions from food.') },
  { value: 0, display: t('Don\'t include emissions from food.') },
];

const RideMyBike = ({
  t,
  isMetric,
  widgetSource,
  vehicles,
  rideMyBikeTradeInVehicle,
  updateTradeInVehicle,
  resultTakeactionRideMyBikeTotalcalories,
  resultTakeactionRideMyBikeGhgper100cal,
  resultTakeactionRideMyBikeBikeghgs,
  resultTakeactionRideMyBikeDriveghgs,
  inputTakeactionRideMyBikeMiles,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        {t('I will ride my bike')}
        <InlineInput
          apiKey={'input_takeaction_ride_my_bike_miles'}
        />
        {t('{{distanceUnit}} per week instead of driving', { distanceUnit: isMetric ? t('km') : t('mi') })}
        <InlineSelect
          ariaLabel={t('vehicle')}
          options={vehicleTradeInOptions(vehicles, t)}
          inputValue={makeInputValue(rideMyBikeTradeInVehicle.index, 1)}
          onChange={value => updateTradeInVehicle('ride_my_bike', value)}
        />
        {t('which has a fuel economy of {{efficiencyDisplay}} {{fuelEfficiencyUnit}}.', rideMyBikeTradeInVehicle)}
      </p>
    </div>
    <div className="action-details__facts">
      {t('Your bicycle can make you a lean, green clean-moving machine! Free tools like Google Maps have bike-friendly directions to get you going as an exemplary neighborhood cyclist.')}
    </div>
    {widgetSource !== 'ontario' &&
      <div className="action-details__links">
        <a
          href="http://www.coolcalifornia.org/funding-wizard#search//%255BZipcode%253D%253D94720%255D%255BCategory%253D%253DTransportation%255D%255BEligible+Applicants%253D%253DIndividual%255D/false/none/false/1/12"
          target="_blank"
          rel="noopener noreferrer"
        >{t('Find rebates and incentives for this action')}</a>
      </div>
    }
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('Price of gasoline:')}
          <InlineInput
            currency
            apiKey="input_takeaction_assumption_price_of_gasoline"
          />
          {t('per {{volumeUnit}}', { volumeUnit: isMetric ? t('L', { ns: 'units' }) : t('gal', { ns: 'units' }) })}
        </li>
        <li>
          {t('We assume getting more exercise means you will eat more food, so we need to consider the carbon footprint of that food.')}
        </li>
        <li
          dangerouslySetInnerHTML={{
            __html: t('You currently consume {{resultTakeactionRideMyBikeTotalcalories}} calories per day with a corresponding carbon footprint of about {{resultTakeactionRideMyBikeGhgper100cal}} grams of CO<sub>2</sub>eq per 100 calories', {
              resultTakeactionRideMyBikeTotalcalories,
              resultTakeactionRideMyBikeGhgper100cal,
            }),
          }}
        />
        <li
          dangerouslySetInnerHTML={{
            __html: t('Riding your bicycle {{displayTakeactionRideMyBikeMiles}} {{distanceUnit}} per week therefore produces about {{resultTakeactionRideMyBikeBikeghgs}} mtCO<sub>2</sub>eq/yr.', {
              inputTakeactionRideMyBikeMiles: inputTakeactionRideMyBikeMiles.ensuredDisplay,
              resultTakeactionRideMyBikeBikeghgs,
            }),
          }}
        />
        <li
          dangerouslySetInnerHTML={{
            __html: t('Driving your vehicle the same distance produces {{resultTakeactionRideMyBikeDriveghgs}} mtCO<sub>2</sub>eq/yr.', {
              resultTakeactionRideMyBikeDriveghgs,
            }),
          }}
        />
      </ul>
      <p>{t('Growing, processing and distributing food produces greenhouse gases. The average American consumes about about 300 - 500 unnecessary calories per day, and riding a bicycle burns about 400 calories per hour, so most people could ride a bicycle for about one hour per day without increasing calorie intake. However, if you do not take in surplus calories and would need to eat more to fuel bicycle riding, then select Include emissions from food.')}</p>
      <p>
        <InlineSelect
          options={foodEmissionOptions(t)}
          apiKey={'input_takeaction_ride_my_bike_include_foodemissions'}
        />
      </p>
    </div>
  </div>
);

RideMyBike.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  updateTradeInVehicle: PropTypes.func.isRequired,
  vehicles: types.Vehicles.isRequired,
  rideMyBikeTradeInVehicle: types.Vehicle.isRequired,
  isMetric: PropTypes.bool.isRequired,
  widgetSource: PropTypes.string.isRequired,
  inputTakeactionRideMyBikeMiles: types.UserInput.isRequired,
  resultTakeactionRideMyBikeTotalcalories: PropTypes.string.isRequired,
  resultTakeactionRideMyBikeGhgper100cal: PropTypes.string.isRequired,
  resultTakeactionRideMyBikeBikeghgs: PropTypes.string.isRequired,
  resultTakeactionRideMyBikeDriveghgs: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => [
  'resultTakeactionRideMyBikeTotalcalories',
  'resultTakeactionRideMyBikeBikeghgs',
  'resultTakeactionRideMyBikeDriveghgs',
].reduce((resultProps, resultKey) => ({
  ...resultProps,
  [resultKey]: s.results(resultKey)(state, ownProps),
}), {
  resultTakeactionRideMyBikeGhgper100cal:
    s.results('result_takeaction_ride_my_bike_ghgper100cal', true)(state, ownProps),
  isMetric: s.isMetric(state),
  widgetSource: s.widgetSource(state),
  vehicles: s.vehicles(state, ownProps),
  rideMyBikeTradeInVehicle: s.rideMyBikeTradeInVehicle(state, ownProps),
  inputTakeactionRideMyBikeMiles: s.inputTakeactionRideMyBikeMiles(state),
});

const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, [
    'updateTradeInVehicle',
  ]),
  dispatch,
);

export default wrap(
  withT('rideMyBike'),
  connect(mapStateToProps, mapDispatchToProps)(RideMyBike),
);
